import classNames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Icon, { COLORS, ICONS } from '../../general-components/icon';
import type { OnsiteLocation } from '../../../stores/types/video';
import { OnsiteLocationSessionRoute, SrtStreamStatus } from '../../../stores/types/video';
import { LOCATION_PUBLISHING_ROUTES } from '../../../stores/reducers/video';
import { GreenroomState as GS } from '../../../stores/types/types';
import { setOnsiteLocationSession } from '../../../stores/actions/video';
import Socket from '../../../connection/socket';
import { setModalState } from '../../../stores/actions/modal';
import { MODAL_TEMPLATES } from '../../../stores/reducers/modal';
import { CreateSrtStream, UpdateOnsiteLocation } from '../../../connection/video';
import useClickAwayListener from '../../../hooks/use-click-away-listener';
import { SRT_STREAM_URL } from '../../meta-menu/meta-onsite-location';
import { showAlert } from '../../general-components/alert-service';
import { STATIC_ASSET_URL } from '../../../utils/content-templates';
import { EntityType } from '../../../types/enums';
import { IconArrowDown2Solid, IconCopySolid } from '@brandlive1941/pixel';
import { isLivekitIngressInputType, LIVEKIT_INGRESS_INPUT_LABELS } from '../../../stores/types/livekit';
import logger from '../../../utils/logger-rum';

interface Props {
	onsiteLocation: OnsiteLocation;
	socket: Socket | null;
	handleOpenCanvas: () => void;
}

export default function OnsiteLocationEntry(props: Props) {
	const {
		onsiteLocation,
		socket,
		handleOpenCanvas,
	} = props;

	const {
		id,
		locationName,
		pathName,
	} = onsiteLocation;

	const dispatch = useDispatch();

	const session = useSelector((s: GS) => s.SessionReducer.session);
	const connectedUsers = useSelector((s: GS) => s.SessionReducer.connectedUsers);
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const onsiteLocations = useSelector((s: GS) => s.VideoReducer.onsiteLocations);
	const srtStreams = useSelector((s: GS) => s.VideoReducer.srtStreams);
	const livekitIngresses = useSelector((s: GS) => s.LivekitReducer.livekitIngresses);
	const { use_whip_streams: useWhipStreams, use_pixel: usePixel } = useSelector((s: GS) => s.SettingsReducer.featureFlags);

	const [locationDropdownOpen, setLocationDropdownOpen] = useState<boolean>(false);
	const [connectionDropdownOpen, setConnectionDropdownOpen] = useState<boolean>(false);
	const [showCredentials, setShowCredentials] = useState<boolean>(false);
	const [creatingSrtStream, setCreatingSrtStream] = useState<boolean>(false);

	const locationDropdownRef = useRef<HTMLDivElement>(null);
	const connectionDropdownRef = useRef<HTMLDivElement>(null);
	const credentialsRef = useRef<HTMLDivElement>(null);

	useClickAwayListener({
		ref: locationDropdownRef,
		onClickAway: () => null,
		onMousedownClickAway: () => setLocationDropdownOpen(false),
		open: locationDropdownOpen,
	});

	useClickAwayListener({
		ref: connectionDropdownRef,
		onClickAway: () => null,
		onMousedownClickAway: () => setConnectionDropdownOpen(false),
		open: connectionDropdownOpen,
	});

	useClickAwayListener({
		ref: credentialsRef,
		onClickAway: () => null,
		onMousedownClickAway: () => setShowCredentials(false),
		open: showCredentials,
	});

	const otherExistingPublisher = useMemo(() => connectedUsers.some((user) => (
		user.session !== session
		&& user.onsiteLocationSession
		&& user.onsiteLocationSession.id === id
		&& LOCATION_PUBLISHING_ROUTES.includes(user.onsiteLocationSession.route)
	)), [connectedUsers, onsiteLocation, session]);

	const effectiveInputType = useMemo(() => {
		if (useWhipStreams && onsiteLocation?.inputType) return onsiteLocation.inputType;
		return onsiteLocation?.useSrt ? 'srt' : 'cam_mic';
	}, [useWhipStreams, onsiteLocation?.inputType, onsiteLocation?.useSrt]);

	const selectedSrtStream = useMemo(() => {
		if (!onsiteLocation?.srtId) return null;
		return srtStreams.find((stream) => stream.id === onsiteLocation.srtId);
	}, [srtStreams, onsiteLocation?.srtId]);

	const ingressInputType = useMemo(
		() => (isLivekitIngressInputType(effectiveInputType) ? effectiveInputType : null),
		[effectiveInputType],
	);

	const selectedIngress = useMemo(() => {
		if (!onsiteLocation?.inputId || !ingressInputType) return null;
		return livekitIngresses.find((i) => i.id === onsiteLocation.inputId) ?? null;
	}, [livekitIngresses, onsiteLocation?.inputId, ingressInputType]);

	useEffect(() => {
		if (creatingSrtStream && onsiteLocation?.srtId) {
			setCreatingSrtStream(false);
		}
	}, [creatingSrtStream, onsiteLocation]);

	const handleSelectLocation = (newLocation: OnsiteLocation) => {
		setLocationDropdownOpen(false);
		const url = new URL(window.location.href);
		window.history.replaceState(
			null,
			'',
			url.href.replace(pathName, newLocation.pathName),
		);
		dispatch(setOnsiteLocationSession({
			id: newLocation.id,
			route: OnsiteLocationSessionRoute.Entry,
		}));
	};

	const handlePublish = () => {
		dispatch(setOnsiteLocationSession({
			id,
			route: OnsiteLocationSessionRoute.Publishing,
		}));
	};

	const handlePublishAttempt = () => {
		if (otherExistingPublisher || selectedSrtStream?.status === SrtStreamStatus.active) {
			dispatch(setModalState({
				modalOpen: true,
				modalTemplate: MODAL_TEMPLATES.MULTI_ONSITE_LOCATION,
				modalData: {
					socket,
					onsiteLocationId: id,
					session,
					handlePublish,
				},
			}));
			return;
		}
		handlePublish();
	};

	const handleUpdateOnsiteLocation = (p: Partial<OnsiteLocation>) => {
		if (!token || !meeting) return;
		UpdateOnsiteLocation(
			token,
			meeting.meeting,
			id,
			p,
		);
	};

	// mirrors the meta panel (meta-onsite-location) — selecting SRT creates a
	// stream for the location when one does not already exist
	const handleSelectSrt = async () => {
		if (!token || !meeting) return;
		setConnectionDropdownOpen(false);

		// guard against a second click while the stream is still being created —
		// srtId is falsy mid-creation, which would otherwise fall through to the
		// else block and clear the connection (inputId: null)
		if (creatingSrtStream) return;

		if (!onsiteLocation.srtId) {
			setCreatingSrtStream(true);
			const entity = await CreateSrtStream(
				token,
				meeting.meeting,
				'SRT Feed',
				`${STATIC_ASSET_URL}/default-images/srt-safety-image.png`,
			);
			if (!entity || entity.entityType !== EntityType.srtStream) {
				logger.error('Failed to create SRT stream');
				setCreatingSrtStream(false);
				return;
			}
			const streamId = Number(entity.entityId);
			handleUpdateOnsiteLocation(useWhipStreams
				? { inputType: 'srt', inputId: String(streamId), srtId: streamId }
				: { srtId: streamId, useSrt: true });
		} else {
			handleUpdateOnsiteLocation(useWhipStreams
				? { inputType: 'srt', inputId: onsiteLocation.srtId ? String(onsiteLocation.srtId) : null }
				: { useSrt: true });
		}
	};

	const copyToClipboard = (value: string, type?: string) => {
		navigator.clipboard.writeText(value);
		showAlert({
			message: type ? 'Copied' : 'Link copied',
			description: type ? `${type} is attached to your clipboard` : 'Onsite URL is attached to your clipboard',
			type: 'general-notification',
			duration: 3000,
		});
	};

	const copyAllCredentials = () => {
		if (!selectedSrtStream) return;
		const streamId = `streamid=${selectedSrtStream.streamKey}`;
		const passphrase = `passphrase=${selectedSrtStream.srtPassphrase}`;
		const srtString = `${SRT_STREAM_URL}:6001?${streamId}&${passphrase}`;
		copyToClipboard(srtString, 'Full URL');
	};

	return (
		<>
			<h4 className="onsite-entry-header">You are joining from:</h4>
			<div className="position-relative" ref={locationDropdownRef}>
				<button
					onClick={() => {
						if (onsiteLocations.length <= 1) return;
						setLocationDropdownOpen(!locationDropdownOpen);
					}}
					className={classNames('onsite-entry-dropdown-button', {
						open: locationDropdownOpen,
						single: onsiteLocations.length === 1,
					})}
				>
					<h2 className="onsite-entry-dropdown-button-text">
						{locationName}
					</h2>
					<div className="flex-center">
						{onsiteLocations.length > 1 && (
							usePixel ? (
								<IconArrowDown2Solid
									width="24"
									height="16"
									className={classNames('onsite-entry-dropdown-caret', locationDropdownOpen && 'open')}
								/>
							) : (
								<Icon
									name={locationDropdownOpen ? ICONS.ACCORDION_UP : ICONS.ACCORDION_DOWN}
									size={24}
									color={COLORS.WHITE}
								/>
							)
						)}
					</div>
				</button>
				<div className={classNames('onsite-entry-dropdown', locationDropdownOpen && 'open')}>
					<div className="onsite-entry-dropdown-option header">
						<span className="bl-font-l3">CHANGE LOCATION</span>
					</div>
					{onsiteLocations.map((location, index) => (
						<div className="onsite-entry-dropdown-option" key={index} onMouseUp={() => handleSelectLocation(location)}>
							<span className="bl-font-p2 truncate-overflow">{location.locationName}</span>
							{id === location.id && (
								<div className="flex-center">
									<Icon name={ICONS.CHECK} size={10} color={COLORS.WHITE} />
								</div>
							)}
						</div>
					))}
				</div>
			</div>
			<span className="onsite-entry-span">
				You can choose both options on the same computer, or use a second computer for each
			</span>
			<div className="onsite-entry-button-row">
				<>
					<div className="position-relative" ref={connectionDropdownRef}>
						<button
							className={classNames('onsite-button', { open: connectionDropdownOpen })}
							onClick={() => setConnectionDropdownOpen(!connectionDropdownOpen)}
						>
							<label className="onsite-button-label grayscale-200">
								{ingressInputType
									? `Using ${LIVEKIT_INGRESS_INPUT_LABELS[ingressInputType]} Endpoint`
									: effectiveInputType === 'srt' ? 'Using SRT Endpoint' : 'Add Cam & Mic'}
							</label>
							<Icon
								name={
									connectionDropdownOpen
										? ICONS.KEYBOARD_ARROW_UP
										: ICONS.KEYBOARD_ARROW_DOWN
								}
								size={12}
								color={COLORS.WHITE}
							/>
						</button>
						<div className={classNames('onsite-entry-dropdown connection', connectionDropdownOpen && 'open')} style={{ width: 'auto', minWidth: '100%' }}>
							<div className="onsite-entry-dropdown-option" onMouseUp={() => {
								handleUpdateOnsiteLocation(useWhipStreams
									? { inputType: 'cam_mic', inputId: null }
									: { useSrt: false });
								setConnectionDropdownOpen(false);
								handlePublishAttempt();
							}}>
								<span className="bl-font-p2">Local Cam & Mic</span>
								{effectiveInputType === 'cam_mic' && (
									<div className="flex-center">
										<Icon name={ICONS.CHECK} size={10} color={COLORS.WHITE} />
									</div>
								)}
							</div>
							<div className="onsite-entry-dropdown-option" onMouseUp={handleSelectSrt}>
								<span className="bl-font-p2">SRT Endpoint</span>
								{effectiveInputType === 'srt' && (
									<div className="flex-center">
										<Icon name={ICONS.CHECK} size={10} color={COLORS.WHITE} />
									</div>
								)}
							</div>
							{useWhipStreams && (['whip', 'rtmp'] as const).map((type) => (
								<div
									key={type}
									className="onsite-entry-dropdown-option"
									onMouseUp={() => {
										// sync provisions (or reconnects) the LiveKit ingress off inputType
										handleUpdateOnsiteLocation({ inputType: type });
										setConnectionDropdownOpen(false);
										setShowCredentials(false);
									}}
								>
									<span className="bl-font-p2">{`${LIVEKIT_INGRESS_INPUT_LABELS[type]} Endpoint`}</span>
									{effectiveInputType === type && (
										<div className="flex-center">
											<Icon name={ICONS.CHECK} size={10} color={COLORS.WHITE} />
										</div>
									)}
								</div>
							))}
						</div>
					</div>
					{ingressInputType && useWhipStreams && (
						<div className="position-relative" ref={credentialsRef}>
							<button className="onsite-button" onClick={() => setShowCredentials(!showCredentials)}>
								<label className="onsite-button-label grayscale-200">
									{`Show ${LIVEKIT_INGRESS_INPUT_LABELS[ingressInputType]} Credentials`}
								</label>
							</button>
							{showCredentials && (
								<div className="onsite-srt-credentials-panel open">
									{selectedIngress ? (
										<>
											<div className="onsite-srt-credentials-input">
												<label>URL</label>
												<div className="credential">
													<span>{selectedIngress.endpoint}</span>
													<button
														className="copy-button"
														onClick={() => copyToClipboard(selectedIngress.endpoint, 'Endpoint')}
													>
														<IconCopySolid color="var(--global-icotext-4)" height="12" width="12" />
													</button>
												</div>
											</div>
											{selectedIngress.streamKey && (
												<div className="onsite-srt-credentials-input">
													<label>STREAM KEY</label>
													<div className="credential">
														<span>{selectedIngress.streamKey}</span>
														<button
															className="copy-button"
															onClick={() => copyToClipboard(selectedIngress.streamKey!, 'Stream Key')}
														>
															<IconCopySolid color="var(--global-icotext-4)" height="12" width="12" />
														</button>
													</div>
												</div>
											)}
										</>
									) : (
										<div className="onsite-srt-credentials-input">
											<label>
												{`No ${LIVEKIT_INGRESS_INPUT_LABELS[ingressInputType]} ingress assigned — contact your producer.`}
											</label>
										</div>
									)}
								</div>
							)}
						</div>
					)}
					{effectiveInputType === 'srt' && (
						<div className="position-relative" ref={credentialsRef}>
							<button className="onsite-button" onClick={() => setShowCredentials(!showCredentials)}>
								<label className="onsite-button-label grayscale-200">Show SRT Credentials</label>
							</button>
							{showCredentials && (
								<div className="onsite-srt-credentials-panel open">
									{selectedSrtStream ? (
										<>
											<div className="onsite-srt-credentials-input">
												<label>URL</label>
												<div className="credential">
													<span>{`${SRT_STREAM_URL}:6001`}</span>
													<button
														className="copy-button"
														onClick={() => copyToClipboard(`${SRT_STREAM_URL}:6001`, 'URL')}
													>
														<IconCopySolid color="var(--global-icotext-4)" height="12" width="12" />
													</button>
												</div>
											</div>
											<div className="onsite-srt-credentials-input">
												<label>STREAM ID</label>
												<div className="credential">
													<span>{selectedSrtStream.streamKey}</span>
													<button
														className="copy-button"
														onClick={() => copyToClipboard(selectedSrtStream.streamKey, 'Stream ID')}
													>
														<IconCopySolid color="var(--global-icotext-4)" height="12" width="12" />
													</button>
												</div>
											</div>
											<div className="onsite-srt-credentials-input">
												<label>PASS PHRASE</label>
												<div className="credential">
													<span>{selectedSrtStream.srtPassphrase}</span>
													<button
														className="copy-button"
														onClick={() => copyToClipboard(selectedSrtStream.srtPassphrase, 'Pass Phrase')}
													>
														<IconCopySolid color="var(--global-icotext-4)" height="12" width="12" />
													</button>
												</div>
											</div>
											<div className="onsite-srt-credentials-copy-all">
												<button className="copy-all-button" onClick={copyAllCredentials}>
													Copy all
												</button>
											</div>
										</>
									) : (
										<div className="onsite-srt-credentials-input">
											<label>No SRT stream assigned — contact your producer.</label>
										</div>
									)}
								</div>
							)}
						</div>
					)}
				</>
				<button className="onsite-button gradient" onClick={handleOpenCanvas}>
					<label className="onsite-button-label grayscale-1200">Open canvas in new window</label>
					<Icon name={ICONS.EXPAND} size={12} color={COLORS.TEXT_GRAY_1200} />
				</button>
			</div>
		</>
	);
}
